import { useMutation } from '@apollo/client';
import {
  CreatePublicApiKeyInput,
  PublicApiAllowedOperation,
  PublicApiKeyItem,
} from '@provision/entity-types';
import { useCallback } from 'react';
import {
  CREATE_PUBLIC_API_KEY_MUTATION,
  GET_PUBLIC_API_KEYS_QUERY,
} from 'src/graphql/entities/public-api-key/public-api-key.operations';
import { updateQueryWithInvalidation } from 'src/graphql/entities/public-api-key/public-api-key.utils';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';

interface CreatePublicApiKeyMutation {
  createPublicApiKey: PublicApiKeyItem;
}

interface CreatePublicApiKeyMutationVariables {
  params: CreatePublicApiKeyInput;
}

export const useCreatePublicApiKey = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showErrorAlert } = useNotifications();

  const [createMutation, { loading: isCreating }] = useMutation<
    CreatePublicApiKeyMutation,
    CreatePublicApiKeyMutationVariables
  >(CREATE_PUBLIC_API_KEY_MUTATION, {
    refetchQueries: [GET_PUBLIC_API_KEYS_QUERY],
    awaitRefetchQueries: true,
    update: updateQueryWithInvalidation(selectedPartnerId!),
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to create API key, please try again');
    },
  });

  const createPublicApiKey = useCallback(
    async (params: {
      name: string;
      description?: string;
      allowedOperations: PublicApiAllowedOperation[];
    }) => {
      if (!selectedPartnerId) {
        throw new Error('Cannot create API key without a selected partner.');
      }
      const result = await createMutation({
        variables: {
          params: {
            partnerId: selectedPartnerId,
            name: params.name,
            description: params.description,
            allowedOperations: params.allowedOperations,
          },
        },
      });
      return result.data?.createPublicApiKey;
    },
    [createMutation, selectedPartnerId],
  );

  return { createPublicApiKey, isCreating };
};
